import http from './http';
import { request } from './http';

interface Result<T> {
  success: boolean;
  message?: string;
  data: T;
}

let encryptionEnabledCache: boolean | null = null;

/** 加解密是否启用（未启用时下载透传原文） */
export async function isEncryptionEnabled(): Promise<boolean> {
  try {
    const res = await request<Result<{ enabled: boolean }>>(http.get('/encryption/enabled'));
    const r = res as unknown as Result<{ enabled: boolean }>;
    const enabled = !!r.data?.enabled;
    encryptionEnabledCache = enabled;
    return enabled;
  } catch {
    // 查询失败时不缓存 false，避免一次 403 导致后续下载永久跳过加密
    return encryptionEnabledCache === true;
  }
}

export interface DetectEncryptedResult {
  encrypted: boolean;
  provider?: string;
  message?: string;
}

/** 判断上传文件是否为 IP-guard 等加密文档（启用加解密时调用） */
export async function detectEncryptedUploadFile(file: File): Promise<boolean> {
  const enabled = await isEncryptionEnabled();
  if (!enabled) return false;

  const form = new FormData();
  form.append('file', file);
  const res = await request<Result<DetectEncryptedResult>>(http.post('/encryption/detect', form, {
    timeout: 120_000,
  }));
  const r = res as unknown as Result<DetectEncryptedResult>;
  return !!r.data?.encrypted;
}

async function throwIfBlobIsApiError(blob: Blob): Promise<void> {
  const type = (blob.type || '').toLowerCase();
  if (!type.includes('json') && !type.includes('text/plain') && !type.includes('text/html')) {
    return;
  }
  const text = await blob.text();
  const trimmed = text.trim();
  if (!trimmed) return;
  try {
    const parsed = JSON.parse(trimmed) as { message?: string; error?: string };
    const msg = parsed.message || parsed.error;
    if (msg) throw new Error(msg);
  } catch (e) {
    if (e instanceof Error && e.message && e.message !== 'Unexpected token') {
      throw e;
    }
    if (trimmed.length < 500) {
      throw new Error(trimmed);
    }
  }
}

/**
 * 浏览器端生成的文件交付前同步加密。
 * 始终请求后端；由服务端根据是否启用加解密决定加密或透传，避免前端误判跳过加密。
 */
export async function encryptFileForDownload(file: Blob, fileName: string): Promise<Blob> {
  const form = new FormData();
  const named =
    file instanceof File
      ? file
      : new File([file], fileName, { type: file.type || 'application/octet-stream' });
  form.append('file', named);

  const res = await http.post('/encryption/encrypt-for-download', form, {
    responseType: 'blob',
    timeout: 600_000,
  });
  const blob = res.data as Blob;
  await throwIfBlobIsApiError(blob);
  return blob;
}
